import React, { useState, useEffect, useRef } from 'react'

type CollectionCreateModalProps = {
  open: boolean
  onClose: () => void
  onCreate: (title: string) => Promise<boolean>
}

/**
 * Modal de création d'une nouvelle collection
 * Responsabilité unique : formulaire de création
 */
export default function CollectionCreateModal({
  open,
  onClose,
  onCreate,
}: CollectionCreateModalProps) {
  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset et focus à l'ouverture
  useEffect(() => {
    if (open) {
      setTitle('')
      setError(null)
      setSaving(false)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Le titre est requis')
      return
    }
    setSaving(true)
    setError(null)
    const success = await onCreate(title.trim())
    setSaving(false)
    if (success) {
      onClose()
    } else {
      setError('Erreur lors de la création')
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      {/* Modal */}
      <aside className="absolute inset-y-0 right-0 w-full max-w-md border-l border-emerald-100/70 bg-white/95 shadow-2xl dark:border-emerald-900/50 dark:bg-zinc-950/95">
        <div className="flex h-full flex-col">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-emerald-100/70 px-6 py-4 dark:border-emerald-900/50">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                Nouvelle collection
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Créez un regroupement pour organiser vos contenus
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-zinc-200/70 px-3 py-1 text-sm text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Fermer
            </button>
          </header>

          {/* Contenu */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <section className="space-y-3">
              <label 
                htmlFor="collection-title"
                className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400"
              >
                Titre de la collection
              </label>
              <input
                ref={inputRef}
                id="collection-title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Ex: Formation onboarding"
                disabled={saving}
                className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/50"
                data-onboarding="collection-title-input"
              />
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
              <button
                type="submit"
                disabled={saving || !title.trim()}
                className="inline-flex w-full justify-center rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:opacity-50"
              >
                {saving ? 'Création...' : 'Créer la collection'}
              </button>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                Conseils
              </h3>
              <ul className="space-y-3 text-sm text-zinc-600 dark:text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  Regroupez par thématique ou formation pour plus de clarté.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  Les collections WordPress sont synchronisées automatiquement.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  Vous pourrez renommer ou supprimer cette collection plus tard.
                </li>
              </ul>
            </section>
          </form>
        </div>
      </aside>
    </div>
  )
}
